import { primitiveTypeUniq } from '@mews/fp';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';
import { includesLowerCase } from '../../../common/utils';
import { AttachmentsTableFilterValues } from '../AttachmentLibraryFilters';
import { AttachmentLibraryTableData } from '../types';
import { getFileExtensionMimeType } from '../utils';

interface UseFilterAttachmentsTableDataProps {
    filters?: AttachmentsTableFilterValues;
    tableSearchValue?: string;
}

export const tableMockData: AttachmentLibraryTableData = [
    {
        id: '1',
        name: 'Lease Agreement',
        type: 'application/pdf',
        size: '1.2 MB',
        serviceIds: ['c8738ef4-b905-402a-8a91-af650087cce8'],
    },
    {
        id: '2',
        name: 'Code of Conduct',
        type: 'image/jpeg',
        size: '2.3 MB',
        serviceIds: ['c8738ef4-b905-402a-8a91-af650087cce8', '6e2fa64b-15bb-4e50-a324-b22d0084e41e'],
    },
    {
        id: '3',
        name: 'Welcome package',
        type: 'image/png',
        size: '1.8 MB',
        serviceIds: ['c8738ef4-b905-402a-8a91-af650087cce8'],
    },
];

const filterBySearchValue = ({
    data,
    searchValue,
    serviceNamesById,
}: {
    data: AttachmentLibraryTableData;
    searchValue: string;
    serviceNamesById: Record<string, string>;
}): AttachmentLibraryTableData =>
    data.filter((attachment) => {
        const { name, type, serviceIds } = attachment;
        return (
            includesLowerCase(name, searchValue) ||
            includesLowerCase(type, searchValue) ||
            serviceIds.some((id) => includesLowerCase(serviceNamesById[id], searchValue))
        );
    });

const filterByFilters = ({
    data,
    filters,
}: {
    data: AttachmentLibraryTableData;
    filters: AttachmentsTableFilterValues;
}): AttachmentLibraryTableData => {
    const { name: filterName, service: filterService, type: filterType } = filters;
    return data.filter((attachment) => {
        const { name, type, serviceIds } = attachment;
        const matchesName = filterName == null || filterName === '' || includesLowerCase(name, filterName);
        const matchesService = filterService == null || serviceIds.some((id) => filterService?.includes(id));
        const matchesType = filterType == null || filterType?.includes(getFileExtensionMimeType(type));
        return matchesName && matchesService && matchesType;
    });
};

export const useFilteredAttachmentsTableData = ({
    filters,
    tableSearchValue,
}: UseFilterAttachmentsTableDataProps): AttachmentLibraryTableData => {
    const data = tableMockData;
    const uniqueServiceIds = primitiveTypeUniq(data.flatMap((attachment) => attachment.serviceIds));
    const { data: serviceNamesById } = useGetServiceNamesByIdQuery(uniqueServiceIds);

    const dataFilteredByFilters = filters != null ? filterByFilters({ data, filters }) : data;

    return tableSearchValue != null
        ? filterBySearchValue({ data, searchValue: tableSearchValue, serviceNamesById })
        : dataFilteredByFilters;
};
